import React from 'react';
import { format } from 'date-fns';
import { Button, Dialog, DialogContent, DialogDescription, DialogTitle } from '@onward-delivery/design';

import BadgeTag from './BadgeTag';

import { BADGES, REVIEW_DECISIONS, SENT_BACK_FOR_REVIEW } from '../constants';
import { latestDecision } from '../utils';

const reviewerName = (review) => review.reviewer?.username || review.reviewer?.email || 'Unknown user';

// The card badge for each decision, so a decision looks the same here as on the contractor's card.
const DECISION_BADGES = {
    APPROVED: BADGES.find((badge) => badge.key === 'APPROVED'),
    DENIED: BADGES.find((badge) => badge.key === 'REJECTED'),
    PENDING: SENT_BACK_FOR_REVIEW,
};

const DecisionBadge = ({ status }) => {
    const badge = DECISION_BADGES[status];
    if (!badge) return <span className="text-[13px] font-semibold text-slate-900">{status}</span>;
    return <BadgeTag variant={badge.variant}>{badge.label}</BadgeTag>;
};

const ReviewEntry = ({ review }) => (
    <li className="flex min-w-0 flex-col gap-1 rounded-md border border-slate-200 bg-white px-3 py-2.5">
        <div className="flex items-center justify-between gap-3 text-[13px]">
            <DecisionBadge status={review.status} />
            <span className="text-slate-500">{format(new Date(review.reviewed_at), 'MMM d, yyyy h:mm a')}</span>
        </div>
        <span className="text-[12px] text-slate-500">By {reviewerName(review)}</span>
        <p className="whitespace-pre-wrap text-[13px] text-slate-700 [overflow-wrap:anywhere]">
            {review.note || 'No note was left.'}
        </p>
    </li>
);

/**
 * Read-only compliance reviews for one contractor. `mode` 'decision' shows the latest decision (View Approval / View
 * Rejection); 'history' shows every review, newest first, including the PENDING ones a document upload writes (View
 * Compliance History).
 *
 * @param {{ viewing: import('../hooks').Viewing|null, onClose: Function }} props
 */
const ReviewHistoryDialog = ({ viewing, onClose }) => {
    const row = viewing?.row;
    const latest = row ? latestDecision(row) : null;
    const reviews = viewing?.mode === 'history' ? row?.compliance_reviews || [] : latest ? [latest] : [];
    const title =
        viewing?.mode === 'history'
            ? 'Compliance History'
            : REVIEW_DECISIONS[latest?.status]?.view.replace('View ', '');

    return (
        <Dialog open={!!row} onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="max-w-lg">
                <DialogTitle className="text-lg font-semibold">{title}</DialogTitle>
                <DialogDescription className="text-sm text-muted">{row?.carrier?.business_name}</DialogDescription>
                <ul className="mt-3 flex max-h-[60vh] flex-col gap-2 overflow-y-auto">
                    {reviews.map((review) => (
                        <ReviewEntry key={review.review_id} review={review} />
                    ))}
                </ul>
                <div className="mt-4 flex justify-end">
                    <Button variant="outline" onClick={onClose}>
                        Close
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default ReviewHistoryDialog;
